/**
 * WCAG 2.1 colour contrast, mirroring the backend's check (`app/common/contrast.py`) so the Branding
 * settings page can show a live warning before the owner submits, instead of only after a 422.
 */

const AA_NORMAL_TEXT = 4.5;

export interface ContrastCheck {
  colour_hex: string;
  ratio_white: number;
  ratio_black: number;
  passes_white: boolean;
  passes_black: boolean;
  /** The gate used server-side: contrast against white only (see the backend module for why). */
  passes_aa: boolean;
}

function parseHex(value: string): [number, number, number] | null {
  const match = /^#?([0-9a-fA-F]{6})$/.exec(value.trim());
  const hex = match?.[1];
  if (!hex) return null;
  return [parseInt(hex.slice(0, 2), 16), parseInt(hex.slice(2, 4), 16), parseInt(hex.slice(4, 6), 16)];
}

function channelLuminance(channel: number): number {
  const c = channel / 255;
  return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

function relativeLuminance([r, g, b]: [number, number, number]): number {
  return 0.2126 * channelLuminance(r) + 0.7152 * channelLuminance(g) + 0.0722 * channelLuminance(b);
}

export function contrastRatio(a: [number, number, number], b: [number, number, number]): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const lighter = Math.max(la, lb);
  const darker = Math.min(la, lb);
  return (lighter + 0.05) / (darker + 0.05);
}

/** Null for anything that isn't a valid 6-digit hex colour. */
export function checkContrast(colourHex: string): ContrastCheck | null {
  const rgb = parseHex(colourHex);
  if (!rgb) return null;
  const ratioWhite = Math.round(contrastRatio(rgb, [255, 255, 255]) * 100) / 100;
  const ratioBlack = Math.round(contrastRatio(rgb, [0, 0, 0]) * 100) / 100;
  const passesWhite = ratioWhite >= AA_NORMAL_TEXT;
  const passesBlack = ratioBlack >= AA_NORMAL_TEXT;
  return {
    colour_hex: `#${rgb.map((c) => c.toString(16).padStart(2, "0")).join("")}`,
    ratio_white: ratioWhite,
    ratio_black: ratioBlack,
    passes_white: passesWhite,
    passes_black: passesBlack,
    passes_aa: passesWhite,
  };
}
